'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { X, Laptop, DollarSign, ChevronDown, Check, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PokerSiteLogo } from '../Dashboard/PokerSiteLogo';
import { cn } from '@/lib/utils';
import { POKER_SITES_ONLINE } from '@/lib/poker-sites';
import { SUPPORTED_CURRENCIES } from '@/lib/currencies';

interface AddBankrollModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type BankrollType = string;

export const AddBankrollModal: React.FC<AddBankrollModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const t = useTranslations('bankrolls.addModal');
  const [bankrollType, setBankrollType] = useState<BankrollType>('Online');
  const [siteName, setSiteName] = useState('');
  const [capitalName, setCapitalName] = useState('');
  const [initialBalance, setInitialBalance] = useState('1000');
  const [currency, setCurrency] = useState('EUR');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const siteDropdownRef = useRef<HTMLDivElement>(null);
  const [customSite, setCustomSite] = useState('');
  const [showCustomSiteInput, setShowCustomSiteInput] = useState(false);
  const [bankrollTypes, setBankrollTypes] = useState<string[]>(['Online', 'Capital']);
  const [pokerSites, setPokerSites] = useState<string[]>([...POKER_SITES_ONLINE]);
  const [currencies, setCurrencies] = useState(SUPPORTED_CURRENCIES);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Fermer le dropdown site quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (siteDropdownRef.current && !siteDropdownRef.current.contains(event.target as Node)) {
        setIsSiteDropdownOpen(false);
      }
    };

    if (isSiteDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSiteDropdownOpen]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const response = await fetch('/api/reference-data');
        const result = await response.json();
        if (!response.ok || !result?.data) return;
        if (Array.isArray(result.data.bankrollTypes) && result.data.bankrollTypes.length > 0) {
          setBankrollTypes(result.data.bankrollTypes);
        }
        if (Array.isArray(result.data.pokerSites) && result.data.pokerSites.length > 0) {
          setPokerSites(result.data.pokerSites);
        }
        if (Array.isArray(result.data.currencies) && result.data.currencies.length > 0) {
          setCurrencies(result.data.currencies);
          if (!result.data.currencies.some((c: { code: string }) => c.code === currency)) {
            setCurrency(result.data.currencies[0].code);
          }
        }
      } catch {
        // fallback silencieux sur valeurs par défaut
      }
    };
    if (isOpen) loadReferenceData();
  }, [isOpen, currency]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Déterminer le nom selon le type
    let finalName = '';
    if (bankrollType === 'Capital') {
      if (!capitalName.trim()) {
        setError('Le nom du capital est requis');
        return;
      }
      finalName = capitalName.trim();
    } else {
      // Online
      finalName = customSite.trim() || siteName;
      if (!finalName) {
        setError(t('errors.siteRequired'));
        return;
      }
    }

    if (!initialBalance || parseFloat(initialBalance) <= 0) {
      setError(t('errors.balanceRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/bankrolls', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: bankrollType,
          siteName: finalName,
          initialBalance: parseFloat(initialBalance),
          currency,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('API Error:', data);
        throw new Error(data.error || t('errors.generic'));
      }


      // Reset form
      setSiteName('');
      setCapitalName('');
      setCustomSite('');
      setShowCustomSiteInput(false);
      setInitialBalance('1000');
      setCurrency('EUR');
      setBankrollType('Online');
      
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err instanceof Error ? err.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none">
        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg my-auto pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {t('title')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Bankroll Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t('typeLabel')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setBankrollType((bankrollTypes.includes('Online') ? 'Online' : bankrollTypes[0] || 'Online') as BankrollType);
                    setCapitalName('');
                    setShowCustomSiteInput(false);
                  }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    bankrollType === 'Online'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Laptop className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white uppercase">
                    {t('online')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBankrollType((bankrollTypes.includes('Capital') ? 'Capital' : bankrollTypes[0] || 'Capital') as BankrollType);
                    setSiteName('');
                  }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    bankrollType === 'Capital'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    CAPITAL
                  </span>
                </button>
              </div>
              {bankrollTypes.length > 2 && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-500 mb-1">Type personnalisé</label>
                  <select
                    value={bankrollType}
                    onChange={(e) => setBankrollType(e.target.value as BankrollType)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    {bankrollTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Capital Name Input (for Capital type) */}
            {bankrollType === 'Capital' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Capital poker (hors bankroll Online)
                </label>
                <Input
                  type="text"
                  value={capitalName}
                  onChange={(e) => setCapitalName(e.target.value)}
                  className="w-full"
                  placeholder="Ex: CRYPTO, BANQUE, etc."
                  required
                />
              </div>
            )}

            {/* Site Selection (for Online type) */}
            {bankrollType === 'Online' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('siteLabel')}
                </label>
                <div ref={siteDropdownRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setIsSiteDropdownOpen(!isSiteDropdownOpen)}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 transition-all text-left',
                      (siteName || customSite)
                        ? 'border-gold bg-gold/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {customSite ? (
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {customSite}
                        </span>
                      ) : siteName ? (
                        <>
                          <PokerSiteLogo siteName={siteName} size={32} className="w-8 h-8 flex-shrink-0" />
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {siteName}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">
                          {t('sitePlaceholder')}
                        </span>
                      )}
                    </div>
                    <ChevronDown className={cn(
                      'w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform',
                      isSiteDropdownOpen && 'rotate-180'
                    )} />
                  </button>

                  {isSiteDropdownOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                      <div className="p-2">
                        {/* Bouton pour saisir un site personnalisé */}
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomSiteInput(true);
                            setSiteName('');
                            setIsSiteDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 mb-2"
                        >
                          <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                          <span className="flex-1 text-left font-medium text-gray-900 dark:text-white">
                            {t('customSiteCta')}
                          </span>
                        </button>
                        
                        {/* Liste des sites */}
                        {pokerSites.map((site) => (
                          <button
                            key={site}
                            type="button"
                            onClick={() => {
                              setSiteName(site);
                              setCustomSite('');
                              setShowCustomSiteInput(false);
                              setIsSiteDropdownOpen(false);
                            }}
                            className={cn(
                              'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-gray-50 dark:hover:bg-gray-700',
                              siteName === site && 'bg-gold/10'
                            )}
                          >
                            <PokerSiteLogo siteName={site} size={32} className="w-8 h-8 flex-shrink-0" />
                            <span className="flex-1 text-left font-medium text-gray-900 dark:text-white">
                              {site}
                            </span>
                            {siteName === site && (
                              <Check className="w-5 h-5 text-gold flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Input pour site personnalisé */}
                  {showCustomSiteInput && (
                    <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={customSite}
                          onChange={(e) => {
                            setCustomSite(e.target.value);
                            setSiteName('');
                            setError('');
                          }}
                          placeholder="Ex: Mon site de poker"
                          className="flex-1"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (customSite.trim()) {
                                setIsSiteDropdownOpen(false);
                                setShowCustomSiteInput(false);
                              }
                            }
                            if (e.key === 'Escape') {
                              setShowCustomSiteInput(false);
                              setCustomSite('');
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomSiteInput(false);
                            setCustomSite('');
                          }}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Initial Balance / Capital */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {bankrollType === 'Capital' ? 'Capital' : t('balanceLabel')}
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={initialBalance}
                  onChange={(e) => setInitialBalance(e.target.value)}
                  className="flex-1"
                  placeholder="1000"
                  required
                />
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  {currencies.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.code}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('balanceHint')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white font-semibold"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('adding') : t('add')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

